import {
  type ChainConfig,
  type Hash,
  type PoolInfo,
  type PrivacyPoolAccount,
  AccountService,
  DataService,
} from '@michael-leverup/privacy-pools-core-sdk';
import { privacyPoolsCatalog } from '@ui/services/privacyPools/config';
import type { Hex } from 'viem';

import {
  type LoadAccountWithIndexedReconstructionResult,
  loadAccountWithIndexedReconstruction,
} from './reconstruction';
import { TERMINAL_CHECKPOINT_ACCOUNT_MARKER } from './terminalCheckpoint';
import { type PoolAccount, ReviewStatus } from './types';

const DEFAULT_LOG_FETCH_CONFIG = new Map<
  number,
  {
    blockChunkSize: number;
    concurrency: number;
    chunkDelayMs: number;
    retryOnFailure: boolean;
    maxRetries: number;
    retryBaseDelayMs: number;
  }
>([
  [
    143,
    {
      blockChunkSize: 1_000,
      concurrency: 1,
      chunkDelayMs: 0,
      retryOnFailure: true,
      maxRetries: 3,
      retryBaseDelayMs: 500,
    },
  ],
  [
    10143,
    {
      blockChunkSize: 1_000,
      concurrency: 1,
      chunkDelayMs: 0,
      retryOnFailure: true,
      maxRetries: 3,
      retryBaseDelayMs: 500,
    },
  ],
]);

const chainConfigById = new Map<number, ChainConfig>();
for (const pool of privacyPoolsCatalog) {
  const previous = chainConfigById.get(pool.chainId);
  if (!previous || pool.deploymentBlock < previous.startBlock) {
    chainConfigById.set(pool.chainId, {
      chainId: pool.chainId,
      privacyPoolAddress: pool.poolAddress as Hex,
      startBlock: pool.deploymentBlock,
      rpcUrl: pool.sdkRpcUrl,
    });
  }
}

const dataService = new DataService(
  [...chainConfigById.values()],
  DEFAULT_LOG_FETCH_CONFIG,
);

const pools: PoolInfo[] = privacyPoolsCatalog.map((pool) => {
  return {
    chainId: pool.chainId,
    address: pool.poolAddress as Hex,
    scope: BigInt(pool.scope) as Hash,
    deploymentBlock: pool.deploymentBlock,
  };
});

const chainIdByScope = new Map(
  privacyPoolsCatalog.map((pool) => {
    return [pool.scope, pool.chainId] as const;
  }),
);

const resolveChainIdByScope = (scope: Hash): number => {
  const chainId = chainIdByScope.get(scope.toString());

  return chainId ?? privacyPoolsCatalog[0]?.chainId ?? 143;
};

const isTerminalCheckpointAccount = (poolAccount: object): boolean => {
  return Boolean(
    (poolAccount as { [TERMINAL_CHECKPOINT_ACCOUNT_MARKER]?: unknown })[
      TERMINAL_CHECKPOINT_ACCOUNT_MARKER
    ],
  );
};

export const createAccount = (seed: string): AccountService => {
  return new AccountService(dataService, {
    mnemonic: seed,
    poolConcurrency: 1,
  });
};

export const loadAccount = async (
  seed: string,
): Promise<
  LoadAccountWithIndexedReconstructionResult & {
    poolAccounts: PoolAccount[];
    poolAccountsByChainScope: Record<string, PoolAccount[]>;
  }
> => {
  const result = await loadAccountWithIndexedReconstruction(
    dataService,
    seed,
    pools,
  );

  const poolAccountsResult = getPoolAccountsFromAccount(
    result.accountService.account,
  );

  return {
    ...result,
    poolAccounts: poolAccountsResult.poolAccounts,
    poolAccountsByChainScope: poolAccountsResult.poolAccountsByChainScope,
  };
};

export const getPoolAccountsFromAccount = (
  account: PrivacyPoolAccount,
  currentChainId?: number,
): {
  poolAccounts: PoolAccount[];
  poolAccountsByChainScope: Record<string, PoolAccount[]>;
} => {
  const allPoolAccounts: PoolAccount[] = [];

  for (const [scope, accounts] of account.poolAccounts.entries()) {
    let index = 1;
    const chainId = resolveChainIdByScope(scope);

    for (const rawPoolAccount of accounts) {
      const lastCommitment =
        rawPoolAccount.children.length > 0
          ? rawPoolAccount.children[rawPoolAccount.children.length - 1]!
          : rawPoolAccount.deposit;

      const terminalAccount = isTerminalCheckpointAccount(rawPoolAccount);
      let reviewStatus = terminalAccount
        ? ReviewStatus.SPENT
        : ReviewStatus.PENDING;
      let balance = terminalAccount ? 0n : lastCommitment.value;

      if (rawPoolAccount.ragequit) {
        reviewStatus = ReviewStatus.EXITED;
        balance = 0n;
      }

      allPoolAccounts.push({
        ...rawPoolAccount,
        name: index,
        balance,
        isValid: false,
        reviewStatus,
        lastCommitment,
        chainId,
        scope,
      });

      index += 1;
    }
  }

  const poolAccountsByChainScope = allPoolAccounts.reduce<
    Record<string, PoolAccount[]>
  >((accumulator, poolAccount) => {
    const key = `${poolAccount.chainId}-${poolAccount.scope}`;
    const previous = accumulator[key] ?? [];

    return {
      ...accumulator,
      [key]: [...previous, poolAccount],
    };
  }, {});

  const poolAccounts =
    typeof currentChainId === 'number'
      ? allPoolAccounts.filter(
          (poolAccount) => poolAccount.chainId === currentChainId,
        )
      : allPoolAccounts;

  return {
    poolAccounts,
    poolAccountsByChainScope,
  };
};
